import { useEffect, useRef } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useCart from 'shared/hooks/use-cart';
import { tracker } from '..';
import { GTM_V2_FLAG } from '../constants/feature-flags';

/**
 * Syncs the GTM data layer after every server cart recalculation and fires
 * deferred add_to_cart / remove_from_cart events so the ecommerce value
 * reflects the server-calculated total (including discounts and promos).
 */
export const useCartItemChangeMonitor = (): void => {
  const Cart = useCart();
  const flags = useFlags();
  const useV2 = !!flags[GTM_V2_FLAG];

  const isCalculating = useObserver(() => Cart.isCalculating);
  const pendingEvents = useObserver(() => Cart.pendingAnalyticsEvents);
  const costBreakdownSubtotal = useObserver(() => (useV2 ? Cart.costBreakdown?.total : Cart.costBreakdown?.subtotal));
  const pendingCount = pendingEvents?.length ?? 0;

  // Track previous isCalculating to detect true→false transition.
  // Initialize to true so we don't fire on initial mount.
  const prevIsCalculatingRef = useRef<boolean>(true);

  useEffect(() => {
    const wasCalculating = prevIsCalculatingRef.current;
    prevIsCalculatingRef.current = isCalculating;

    if (!useV2) {
      return;
    }

    if (!wasCalculating || isCalculating) {
      return;
    }

    const { cart, checkoutToken, dispensary, deliveryOption, paymentMethod, medicalOrder, reservation } =
      Cart.order ?? {};

    tracker.setContext({
      activeCart: {
        ...cart,
        subtotal: costBreakdownSubtotal,
        checkoutToken,
      },
    });

    if (pendingCount > 0) {
      const events = [...pendingEvents];
      Cart.clearPendingAnalyticsEvents();

      events.forEach((event) => {
        if (event.type === 'add') {
          tracker.addedProductToCart(event.payload);
        } else if (event.type === 'remove') {
          tracker.removedProductFromCart(event.payload);
        }
      });
    }

    tracker.cartUpdated({
      cartId: checkoutToken,
      products: cart,
      dispensaryId: dispensary?.id,
      dispensaryName: dispensary?.name,
      deliveryMethod: deliveryOption ?? undefined,
      paymentType: paymentMethod ?? undefined,
      purchaseType: medicalOrder ? 'medicinal' : 'recreational',
      isScheduled: reservation != null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalculating, pendingCount, useV2, costBreakdownSubtotal]);
};
